/**
 * Copyright 2025 Google LLC.
 * Copyright (c) Microsoft Corporation.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  InvalidArgumentException,
  UnsupportedOperationException,
} from '../../../protocol/protocol.js';
import type {
  EmptyResult,
  Emulation,
  UAClientHints,
} from '../../../protocol/protocol.js';
import type {ContextConfigStorage} from '../browser/ContextConfigStorage.js';
import type {UserContextStorage} from '../browser/UserContextStorage.js';
import type {BrowsingContextImpl} from '../context/BrowsingContextImpl.js';
import type {BrowsingContextStorage} from '../context/BrowsingContextStorage.js';

// Media features supported by Blink's MediaFeatureOverrides:
// https://source.chromium.org/chromium/chromium/src/+/main:third_party/blink/renderer/core/css/media_feature_overrides.cc;l=124
const SUPPORTED_MEDIA_FEATURES = new Set([
  'color-gamut',
  'forced-colors',
  'prefers-color-scheme',
  'prefers-contrast',
  'prefers-reduced-data',
  'prefers-reduced-motion',
  'prefers-reduced-transparency',
]);

export class EmulationProcessor {
  #userContextStorage: UserContextStorage;
  #browsingContextStorage: BrowsingContextStorage;
  #contextConfigStorage: ContextConfigStorage;

  constructor(
    browsingContextStorage: BrowsingContextStorage,
    userContextStorage: UserContextStorage,
    contextConfigStorage: ContextConfigStorage,
  ) {
    this.#userContextStorage = userContextStorage;
    this.#browsingContextStorage = browsingContextStorage;
    this.#contextConfigStorage = contextConfigStorage;
  }

  async setGeolocationOverride(
    params: Emulation.SetGeolocationOverrideParameters,
  ): Promise<EmptyResult> {
    if ('coordinates' in params && 'error' in params) {
      // Unreachable. Handled by params parser.
      throw new InvalidArgumentException(
        'Coordinates and error cannot be set at the same time',
      );
    }

    let geolocation:
      | Emulation.GeolocationCoordinates
      | Emulation.GeolocationPositionError
      | null = null;

    if ('coordinates' in params) {
      if (
        (params.coordinates?.altitude ?? null) === null &&
        (params.coordinates?.altitudeAccuracy ?? null) !== null
      ) {
        throw new InvalidArgumentException(
          'Geolocation altitudeAccuracy can be set only with altitude',
        );
      }

      geolocation = params.coordinates;
    } else if ('error' in params) {
      if (params.error.type !== 'positionUnavailable') {
        // Unreachable. Handled by params parser.
        throw new InvalidArgumentException(
          `Unknown geolocation error ${params.error.type}`,
        );
      }
      geolocation = params.error;
    } else {
      // Unreachable. Handled by params parser.
      throw new InvalidArgumentException(`Coordinates or error should be set`);
    }

    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          geolocation,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        geolocation,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );

        await context.setGeolocationOverride(config.geolocation ?? null);
      }),
    );
    return {};
  }

  async setLocaleOverride(
    params: Emulation.SetLocaleOverrideParameters,
  ): Promise<EmptyResult> {
    const locale = params.locale ?? null;

    if (locale !== null && !isValidLocale(locale)) {
      throw new InvalidArgumentException(`Invalid locale "${locale}"`);
    }

    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          locale,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        locale,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );

        await Promise.all([
          context.setLocaleOverride(config.locale ?? null),
          // Set `AcceptLanguage` to locale.
          context.setUserAgentAndAcceptLanguage(
            config.userAgent,
            config.locale,
            config.clientHints,
          ),
        ]);
      }),
    );
    return {};
  }

  async setMediaFeaturesOverride(
    params: Emulation.SetMediaFeaturesOverrideParameters,
  ): Promise<EmptyResult> {
    const unsupportedFeatures = Object.keys(params.features ?? {}).filter(
      (feature) => !SUPPORTED_MEDIA_FEATURES.has(feature),
    );
    if (unsupportedFeatures.length > 0) {
      throw new UnsupportedOperationException(
        `Media feature(s) not supported: ${unsupportedFeatures.map((f) => `"${f}"`).join(', ')}`,
      );
    }

    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
      true,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          mediaFeatures: params.features,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        mediaFeatures: params.features,
      });
    }

    if (params.contexts === undefined && params.userContexts === undefined) {
      this.#contextConfigStorage.updateGlobalConfig({
        mediaFeatures: params.features,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );

        await context.setMediaFeaturesOverride(config.mediaFeatures ?? null);
      }),
    );
    return {};
  }

  async setScriptingEnabled(
    params: Emulation.SetScriptingEnabledParameters,
  ): Promise<EmptyResult> {
    const scriptingEnabled = params.enabled;

    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          scriptingEnabled,
        },
      );
    }

    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        scriptingEnabled,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );

        await context.setScriptingEnabled(config.scriptingEnabled ?? null);
      }),
    );
    return {};
  }

  async setScrollbarTypeOverride(
    params: Emulation.SetScrollbarTypeOverrideParameters,
  ): Promise<EmptyResult> {
    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
      true,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          scrollbarType: params.scrollbarType,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        scrollbarType: params.scrollbarType,
      });
    }

    if (params.contexts === undefined && params.userContexts === undefined) {
      this.#contextConfigStorage.updateGlobalConfig({
        scrollbarType: params.scrollbarType,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );

        await context.setScrollbarTypeOverride(config.scrollbarType ?? null);
      }),
    );
    return {};
  }

  async setScreenOrientationOverride(
    params: Emulation.SetScreenOrientationOverrideParameters,
  ): Promise<EmptyResult> {
    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          screenOrientation: params.screenOrientation,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        screenOrientation: params.screenOrientation,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );

        await context.setViewport(
          config.viewport ?? null,
          config.devicePixelRatio ?? null,
          config.screenOrientation ?? null,
        );
      }),
    );
    return {};
  }

  async setScreenSettingsOverride(
    params: Emulation.SetScreenSettingsOverrideParameters,
  ): Promise<EmptyResult> {
    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          screenArea: params.screenArea,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        screenArea: params.screenArea,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );

        await context.setViewport(
          config.viewport ?? null,
          config.devicePixelRatio ?? null,
          config.screenOrientation ?? null,
        );
      }),
    );
    return {};
  }

  /**
   * Returns a list of top-level browsing contexts.
   */
  async #getRelatedTopLevelBrowsingContexts(
    browsingContextIds?: string[],
    userContextIds?: string[],
    allowGlobal = false,
  ): Promise<BrowsingContextImpl[]> {
    if (browsingContextIds === undefined && userContextIds === undefined) {
      if (allowGlobal) {
        return this.#browsingContextStorage.getTopLevelContexts();
      }
      throw new InvalidArgumentException(
        'Either user contexts or browsing contexts must be provided',
      );
    }

    if (browsingContextIds !== undefined && userContextIds !== undefined) {
      throw new InvalidArgumentException(
        'User contexts and browsing contexts are mutually exclusive',
      );
    }

    const result = [];
    if (browsingContextIds === undefined) {
      // userContextIds !== undefined
      if (userContextIds!.length === 0) {
        throw new InvalidArgumentException('user context should be provided');
      }

      // Verify that all user contexts exist.
      await this.#userContextStorage.verifyUserContextIdList(userContextIds!);

      for (const userContextId of userContextIds!) {
        const topLevelBrowsingContexts = this.#browsingContextStorage
          .getTopLevelContexts()
          .filter(
            (browsingContext) => browsingContext.userContext === userContextId,
          );
        result.push(...topLevelBrowsingContexts);
      }
    } else {
      if (browsingContextIds.length === 0) {
        throw new InvalidArgumentException(
          'browsing context should be provided',
        );
      }

      for (const browsingContextId of browsingContextIds) {
        const browsingContext =
          this.#browsingContextStorage.getContext(browsingContextId);
        if (!browsingContext.isTopLevelContext()) {
          throw new InvalidArgumentException(
            'The command is only supported on the top-level context',
          );
        }
        result.push(browsingContext);
      }
    }
    // Remove duplicates. Compare `BrowsingContextImpl` by reference is correct here, as
    // `browsingContextStorage` returns the same instance for the same id.
    return [...new Set(result).values()];
  }

  async setTimezoneOverride(
    params: Emulation.SetTimezoneOverrideParameters,
  ): Promise<EmptyResult> {
    let timezone = params.timezone ?? null;

    if (timezone !== null && !isValidTimezone(timezone)) {
      throw new InvalidArgumentException(`Invalid timezone "${timezone}"`);
    }

    if (timezone !== null && isTimeZoneOffsetString(timezone)) {
      // CDP supports offset timezone with `GMT` prefix.
      timezone = `GMT${timezone}`;
    }

    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          timezone,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        timezone,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );
        await context.setTimezoneOverride(config.timezone ?? null);
      }),
    );
    return {};
  }

  async setTouchOverride(
    params: Emulation.SetTouchOverrideParameters,
  ): Promise<EmptyResult> {
    const maxTouchPoints = params.maxTouchPoints;

    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
      true,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          maxTouchPoints,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        maxTouchPoints,
      });
    }

    if (params.contexts === undefined && params.userContexts === undefined) {
      this.#contextConfigStorage.updateGlobalConfig({
        maxTouchPoints,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );
        await context.setTouchOverride(config.maxTouchPoints ?? null);
      }),
    );
    return {};
  }

  async setUserAgentOverrideParams(
    params: Emulation.SetUserAgentOverrideParameters,
  ): Promise<EmptyResult> {
    if (params.userAgent === '') {
      throw new UnsupportedOperationException(
        'empty user agent string is not supported',
      );
    }

    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
      true,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          userAgent: params.userAgent,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        userAgent: params.userAgent,
      });
    }

    if (params.contexts === undefined && params.userContexts === undefined) {
      this.#contextConfigStorage.updateGlobalConfig({
        userAgent: params.userAgent,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );
        await context.setUserAgentAndAcceptLanguage(
          config.userAgent,
          config.locale,
          config.clientHints,
        );
      }),
    );
    return {};
  }

  async setClientHintsOverride(
    params: UAClientHints.UserAgentClientHints.SetClientHintsOverrideCommand['params'],
  ): Promise<EmptyResult> {
    const clientHints = params.clientHints ?? null;

    // Get all relevant contexts to update:
    // 1. Specific browsing contexts (if provided).
    // 2. All contexts for specific user contexts (if provided).
    // 3. All top-level contexts (if global).
    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
      true,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          clientHints,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        clientHints,
      });
    }

    if (params.contexts === undefined && params.userContexts === undefined) {
      this.#contextConfigStorage.updateGlobalConfig({
        clientHints,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );
        await context.setUserAgentAndAcceptLanguage(
          config.userAgent,
          config.locale,
          config.clientHints,
        );
      }),
    );
    return {};
  }

  async setNetworkConditions(
    params: Emulation.SetNetworkConditionsParameters,
  ): Promise<EmptyResult> {
    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
      true,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          emulatedNetworkConditions: params.networkConditions,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        emulatedNetworkConditions: params.networkConditions,
      });
    }

    if (params.contexts === undefined && params.userContexts === undefined) {
      this.#contextConfigStorage.updateGlobalConfig({
        emulatedNetworkConditions: params.networkConditions,
      });
    }

    if (
      params.networkConditions !== null &&
      params.networkConditions.type !== 'offline'
    ) {
      throw new UnsupportedOperationException(
        `Unsupported network conditions ${params.networkConditions.type}`,
      );
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );

        await context.setEmulatedNetworkConditions(
          config.emulatedNetworkConditions ?? null,
        );
      }),
    );
    return {};
  }

  async setViewportMetaOverride(
    params: Emulation.SetViewportMetaOverrideParameters,
  ): Promise<EmptyResult> {
    const browsingContexts = await this.#getRelatedTopLevelBrowsingContexts(
      params.contexts,
      params.userContexts,
      true,
    );

    for (const browsingContextId of params.contexts ?? []) {
      this.#contextConfigStorage.updateBrowsingContextConfig(
        browsingContextId,
        {
          viewportMeta: params.viewportMeta,
        },
      );
    }
    for (const userContextId of params.userContexts ?? []) {
      this.#contextConfigStorage.updateUserContextConfig(userContextId, {
        viewportMeta: params.viewportMeta,
      });
    }

    if (params.contexts === undefined && params.userContexts === undefined) {
      this.#contextConfigStorage.updateGlobalConfig({
        viewportMeta: params.viewportMeta,
      });
    }

    await Promise.all(
      browsingContexts.map(async (context) => {
        // Actual value can be different from the one in params, e.g. in case of already
        // existing more granular setting.
        const config = this.#contextConfigStorage.getActiveConfig(
          context.id,
          context.userContext,
        );

        await context.setViewportMetaOverride(config.viewportMeta ?? null);
      }),
    );
    return {};
  }
}

// Export for testing.
export function isValidLocale(locale: string): boolean {
  try {
    new Intl.Locale(locale);
    return true;
  } catch (e) {
    if (e instanceof RangeError) {
      return false;
    }
    // Re-throw other errors
    throw e;
  }
}

// Export for testing.
export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, {timeZone: timezone});
    return true;
  } catch (e) {
    if (e instanceof RangeError) {
      return false;
    }
    // Re-throw other errors
    throw e;
  }
}

// Export for testing.
export function isTimeZoneOffsetString(timezone: string): boolean {
  return /^[+-](?:2[0-3]|[01]\d)(?::[0-5]\d)?$/.test(timezone);
}
